import {
	useEffect,
	useState,
	useMemo,
	useCallback,
	useRef,
	memo,
	useImperativeHandle,
	forwardRef,
} from "react";
import { useTranslate } from "react-admin";
import { useForm, useFormState } from "react-final-form";
import {
	Box,
	Button,
	Card,
	CardContent,
	Typography,
	TextField,
	IconButton,
	Grid,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import { CustomTranslatableInputs } from "./surveySharedComponents";

export const FEEDBACK_MESSAGES_SOURCE = "feedbackBracketMessages";

const DEFAULT_LOCALES = ["en", "ar(Male)", "ar(Female)"];

const useStyles = makeStyles((theme) => ({
	container: {
		padding: 0,
	},
	infoText: {
		marginBottom: theme.spacing(2),
	},
	maxScore: {
		fontWeight: "bold",
		marginBottom: theme.spacing(2),
	},
	addButton: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(2),
	},
	bracketCard: {
		marginBottom: theme.spacing(2),
		border: `1px solid ${theme.palette.divider}`,
	},
	invalidBracketCard: {
		borderColor: theme.palette.error.main,
	},
	inputField: {
		marginBottom: 0,
	},
	deleteButton: {
		marginTop: theme.spacing(1),
	},
	errorText: {
		display: "block",
		marginTop: theme.spacing(0.5),
	},
}));

const emptyFeedbackMessage = () => ({
	en: "",
	"ar(Male)": "",
	"ar(Female)": "",
});

const isBlank = (value) => !value || `${value}`.trim() === "";

export const normalizeFeedbackMessage = (message) => {
	if (!message) return emptyFeedbackMessage();
	if (typeof message === "string")
		return { ...emptyFeedbackMessage(), en: message };
	return {
		en: message.en ?? "",
		"ar(Male)": message["ar(Male)"] ?? "",
		"ar(Female)": message["ar(Female)"] ?? "",
	};
};

const calculateMaxScore = (questions) => {
	if (!questions || questions.length === 0) return 0;

	return questions.reduce((total, question) => {
		const questionMax =
			question?.answers && question?.answers?.length > 0
				? Math.max(
						...question.answers.map((answer) => answer?.weight || 0)
				  )
				: 0;
		return total + questionMax;
	}, 0);
};

export const validateBracketsComplete = (brackets) => {
	const bracketsArray = Object.values(brackets);

	for (let bracket of bracketsArray) {
		const message = normalizeFeedbackMessage(bracket.feedbackMessage);
		if (
			bracket.fromScore === null ||
			bracket.fromScore === undefined ||
			bracket.toScore === null ||
			bracket.toScore === undefined ||
			isBlank(message.en)
		) {
			return false;
		}
	}
	return true;
};

export const getMissingArabicRanks = (brackets) => {
	const missing = [];
	const bracketsArray = Object.values(brackets);

	for (let bracket of bracketsArray) {
		const message = normalizeFeedbackMessage(bracket.feedbackMessage);
		if (isBlank(message["ar(Male)"]) || isBlank(message["ar(Female)"])) {
			missing.push(bracket.rank);
		}
	}
	return missing;
};

export const getInvalidRangeRanks = (brackets) => {
	const invalid = [];
	const bracketsArray = Object.values(brackets);
	for (let bracket of bracketsArray) {
		if (bracket.fromScore >= bracket.toScore) {
			invalid.push(bracket.rank);
		}
	}
	return invalid;
};

export const checkBracketsOverlap = (brackets) => {
	const bracketsArray = Object.values(brackets);
	for (let i = 0; i < bracketsArray.length; i++) {
		for (let j = i + 1; j < bracketsArray.length; j++) {
			const first = bracketsArray[i];
			const second = bracketsArray[j];

			const firstFrom = first.fromScore;
			const firstTo = first.toScore;
			const secondFrom = second.fromScore;
			const secondTo = second.toScore;

			// Check if ranges overlap
			if (!(firstTo < secondFrom || secondTo < firstFrom)) {
				return true;
			}
		}
	}
	return false;
};

export const getOverlappingRanks = (brackets) => {
	const overlapping = [];
	const bracketsArray = Object.values(brackets);

	for (let i = 0; i < bracketsArray.length; i++) {
		for (let j = i + 1; j < bracketsArray.length; j++) {
			const first = bracketsArray[i];
			const second = bracketsArray[j];

			const firstFrom = first.fromScore;
			const firstTo = first.toScore;
			const secondFrom = second.fromScore;
			const secondTo = second.toScore;

			// Check if ranges overlap
			if (!(firstTo < secondFrom || secondTo < firstFrom)) {
				overlapping.push(first.rank, second.rank);
			}
		}
	}
	return overlapping;
};

export const checkBracketsFullCoverage = (brackets, maxScore) => {
	if (!brackets || Object.keys(brackets).length === 0) return true;

	const bracketsArray = Object.values(brackets).sort(
		(a, b) => a.fromScore - b.fromScore
	);

	if (bracketsArray[0].fromScore !== 1) {
		return false;
	}

	for (let i = 0; i < bracketsArray.length - 1; i++) {
		if (bracketsArray[i].toScore + 1 !== bracketsArray[i + 1].fromScore) {
			return false;
		}
	}

	if (bracketsArray[bracketsArray.length - 1].toScore !== maxScore) {
		return false;
	}

	return true;
};

const ManageFeedbackComponent = forwardRef(({ record, onChange, locales }, ref) => {
	const classes = useStyles();
	const [brackets, setBrackets] = useState({});
	const [overlappingRanks, setOverlappingRanks] = useState([]);
	const [missingArabicRanks, setMissingArabicRanks] = useState([]);
	const translate = useTranslate();
	const form = useForm();
	const { values: formValues } = useFormState();
	const isInitialized = useRef(false);

	const messages = formValues?.[FEEDBACK_MESSAGES_SOURCE];

	const maxScore = useMemo(() => {
		return calculateMaxScore(
			formValues?.allQuestions || record?.allQuestions || []
		);
	}, [formValues?.allQuestions, record?.allQuestions]);

	const translatableLocales = useMemo(
		() => (locales && locales.length ? locales : DEFAULT_LOCALES),
		[locales]
	);

	useImperativeHandle(ref, () => ({
		markOverlapping: (ranksToMark) => {
			setOverlappingRanks(ranksToMark);
		},
		markMissingArabic: (ranksToMark) => {
			setMissingArabicRanks(ranksToMark);
		},
		getMaxScore: () => maxScore,
	}));

	useEffect(() => {
		if (isInitialized.current) return;
		const savedBrackets = record?.feedbackBrackets;
		if (!Array.isArray(savedBrackets) || savedBrackets.length === 0) return;
		isInitialized.current = true;

		const initialBrackets = {};
		const initialMessages = {};
		savedBrackets.forEach((bracket, index) => {
			const rank = bracket.rank || `rank_${bracket.id ?? index}`;
			const { feedbackMessage, feedback_message, ...rest } = bracket;
			initialBrackets[rank] = {
				...rest,
				rank,
				fromScore: bracket.fromScore ?? bracket.from_score ?? null,
				toScore: bracket.toScore ?? bracket.to_score ?? null,
			};
			initialMessages[rank] = normalizeFeedbackMessage(
				feedbackMessage ?? feedback_message
			);
		});

		setBrackets(initialBrackets);
		form.change(FEEDBACK_MESSAGES_SOURCE, initialMessages);
	}, [record?.feedbackBrackets, form]);

	useEffect(() => {
		if (!onChange) return;
		const merged = {};
		Object.entries(brackets).forEach(([rank, bracket]) => {
			merged[rank] = {
				...bracket,
				rank,
				feedbackMessage: normalizeFeedbackMessage(messages?.[rank]),
			};
		});
		onChange(merged);
	}, [brackets, messages, onChange]);

	const handleAddBracket = useCallback(() => {
		const rank = `rank_${Date.now()}`;
		setBrackets((prevBrackets) => ({
			...prevBrackets,
			[rank]: {
				rank,
				fromScore: null,
				toScore: null,
			},
		}));
	}, []);

	const handleDeleteBracket = useCallback(
		(rank) => {
			setBrackets((prevBrackets) => {
				const newBrackets = { ...prevBrackets };
				delete newBrackets[rank];
				return newBrackets;
			});
			form.change(`${FEEDBACK_MESSAGES_SOURCE}.${rank}`, undefined);
		},
		[form]
	);

	const handleFromChange = useCallback((rank, value) => {
		setBrackets((prevBrackets) => ({
			...prevBrackets,
			[rank]: {
				...prevBrackets[rank],
				fromScore: value ? parseInt(value) : null,
			},
		}));
	}, []);

	const handleToChange = useCallback((rank, value) => {
		setBrackets((prevBrackets) => ({
			...prevBrackets,
			[rank]: {
				...prevBrackets[rank],
				toScore: value ? parseInt(value) : null,
			},
		}));
	}, []);

	return (
		<Box className={classes.container}>
			<Typography variant="body2" className={classes.infoText}>
				{translate("surveys.feedbackBrackets.description")}
			</Typography>
			<Typography variant="body2" className={classes.maxScore}>
				{translate("surveys.feedbackBrackets.maxScore")}: {maxScore}
			</Typography>

			<Button
				variant="contained"
				color="primary"
				startIcon={<AddIcon />}
				onClick={handleAddBracket}
				className={classes.addButton}
			>
				{translate("surveys.feedbackBrackets.addBracket")}
			</Button>

			{Object.entries(brackets).map(([rank, bracket]) => {
				const isMissingArabic = missingArabicRanks.includes(rank);
				return (
					<Card
						key={rank}
						className={`${classes.bracketCard} ${
							isMissingArabic ? classes.invalidBracketCard : ""
						}`}
					>
						<CardContent>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={5}>
									<TextField
										type="number"
										label={translate(
											"surveys.feedbackBrackets.from"
										)}
										value={bracket.fromScore ?? ""}
										onChange={(e) =>
											handleFromChange(rank, e.target.value)
										}
										fullWidth
										error={overlappingRanks.includes(rank)}
										inputProps={{
											min: 0,
											max: maxScore,
										}}
										className={classes.inputField}
									/>
								</Grid>
								<Grid item xs={12} sm={5}>
									<TextField
										type="number"
										label={translate(
											"surveys.feedbackBrackets.to"
										)}
										value={bracket.toScore ?? ""}
										onChange={(e) =>
											handleToChange(rank, e.target.value)
										}
										fullWidth
										error={overlappingRanks.includes(rank)}
										inputProps={{
											min: 0,
											max: maxScore,
										}}
										className={classes.inputField}
									/>
								</Grid>
								<Grid item xs={12} sm={2}>
									<IconButton
										color="secondary"
										onClick={() => handleDeleteBracket(rank)}
										className={classes.deleteButton}
									>
										<DeleteIcon />
									</IconButton>
								</Grid>
								<Grid item xs={12}>
									<CustomTranslatableInputs
										source={`${FEEDBACK_MESSAGES_SOURCE}.${rank}`}
										locales={translatableLocales}
										label={translate(
											"surveys.feedbackBrackets.feedbackMessage"
										)}
									/>
									{isMissingArabic && (
										<Typography
											variant="caption"
											color="error"
											className={classes.errorText}
										>
											{translate(
												"surveys.feedbackBrackets.arabicRequired"
											)}
										</Typography>
									)}
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				);
			})}
		</Box>
	);
});

export const ManageFeedback = memo(ManageFeedbackComponent);
